"use client";

// The top bar (design spec §2, §1.10): context, not navigation. 56 px, `--card`, one hairline.
// Left: hamburger, company switcher, sidebar-reopen, breadcrumb. Then the focus chips, the ⌘K
// search box and the actions (side panel, Inbox, account). The chips fold into the one row
// when the bar is wide enough (a container query), else they take a second 40 px row (44 on
// phones, scrolling sideways). DOM order is the tab order: left → chips → search → actions.
import type * as React from "react";
import { cn } from "../cn";
import { Button } from "./button";

function Topbar({
  left,
  tabs,
  chips,
  center,
  right,
  className,
}: {
  left?: React.ReactNode;
  /** The mode bar (`ModeTabs`), centred in the first row; the chips then take the second. */
  tabs?: React.ReactNode;
  chips?: React.ReactNode;
  center?: React.ReactNode;
  right?: React.ReactNode;
  className?: string;
}) {
  if (tabs) {
    // Row 1: left | mode tabs (centred on the bar) | search + actions. Row 2 (40 px; 44 on
    // phones, scrolling sideways): where am I, the breadcrumb and the focus chips.
    return (
      <header
        data-slot="topbar"
        data-tabs=""
        className={cn("@container border-b border-border bg-card", className)}
      >
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-3 px-4 max-md:grid-cols-[minmax(0,1fr)_auto] max-sm:gap-x-1 max-sm:px-2">
          <TopbarGroup data-slot="topbar-left" className="h-topbar min-w-0 gap-1">
            {left}
          </TopbarGroup>
          {/* Phones: the tabs take their own row under the switcher and the actions. */}
          <div
            data-slot="topbar-tabs"
            className="flex min-w-0 justify-center max-md:col-span-2 max-md:row-start-2 max-md:justify-start max-md:pb-2"
          >
            {tabs}
          </div>
          <TopbarGroup
            data-slot="topbar-actions"
            className="min-w-0 justify-end gap-1 max-sm:gap-0"
          >
            <div data-slot="topbar-center" className="flex min-w-0 shrink justify-end">
              {center}
            </div>
            {right}
          </TopbarGroup>
        </div>
        {chips ? (
          <div
            data-slot="topbar-chips"
            className="flex h-10 min-w-0 items-center gap-2 strip-scroll border-t border-subtle px-4 max-sm:px-2 pointer-coarse:h-12"
          >
            {chips}
          </div>
        ) : null}
      </header>
    );
  }
  return (
    <header
      data-slot="topbar"
      className={cn("@container border-b border-border bg-card", className)}
    >
      <div
        className={cn(
          "grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 px-4 max-sm:gap-x-1 max-sm:px-2",
          chips ? "@min-[1220px]:grid-cols-[minmax(0,1fr)_auto_auto_auto]" : "",
        )}
      >
        <TopbarGroup
          data-slot="topbar-left"
          className="col-start-1 row-start-1 h-topbar min-w-0 gap-1"
        >
          {left}
        </TopbarGroup>
        {chips ? (
          <div
            data-slot="topbar-chips"
            className={cn(
              "col-span-3 col-start-1 row-start-2 -mx-4 flex h-10 min-w-0 items-center gap-2 strip-scroll border-t border-subtle px-4 max-sm:-mx-2 max-sm:px-2 pointer-coarse:h-12",
              "@min-[1220px]:col-span-1 @min-[1220px]:col-start-2 @min-[1220px]:row-start-1 @min-[1220px]:mx-0 @min-[1220px]:h-topbar @min-[1220px]:overflow-visible @min-[1220px]:border-t-0 @min-[1220px]:px-0",
            )}
          >
            {chips}
          </div>
        ) : null}
        <div
          data-slot="topbar-center"
          className={cn(
            "col-start-2 row-start-1 flex min-w-0 justify-end",
            chips ? "@min-[1220px]:col-start-3" : "",
          )}
        >
          {center}
        </div>
        <TopbarGroup
          data-slot="topbar-actions"
          className={cn(
            "col-start-3 row-start-1 justify-end gap-1 max-sm:gap-0",
            chips ? "@min-[1220px]:col-start-4" : "",
          )}
        >
          {right}
        </TopbarGroup>
      </div>
    </header>
  );
}

function TopbarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="topbar-group" className={cn("flex items-center", className)} {...props} />;
}

/** A 36 px ghost icon button for the bar; a drawn 44 px target on coarse pointers. */
function TopbarIconButton({ className, ...props }: React.ComponentProps<typeof Button>) {
  return (
    <Button
      variant="ghost"
      size="icon"
      data-slot="topbar-icon-button"
      className={cn("relative pointer-coarse:size-11", className)}
      {...props}
    />
  );
}

export { Topbar, TopbarGroup, TopbarIconButton };
